import { useState, type ReactNode } from 'react';
import { UMIcon, UMR } from '../../urbanmap/_components';
import { PLAN } from './tokens';
import type { PlanBootstrap, PlanProject, PlanSpace } from './types';
import { Pill, PlanLogo } from './ui';

function Popover({ children, align = 'left', width = 360 }: { children: ReactNode; align?: 'left' | 'right'; width?: number }) {
  return (
    <div
      style={{
        position: 'absolute', top: 'calc(100% + 4px)', [align]: 0, width, zIndex: 60, background: UMR.white,
        border: `1px solid ${UMR.border}`, boxShadow: '0 6px 24px rgba(0,0,0,0.18)', fontFamily: UMR.font, color: UMR.black,
      }}
    >
      {children}
    </div>
  );
}

function MenuItem({ icon, children, onClick, hint }: { icon: string; children: ReactNode; onClick: () => void; hint?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: UMR.font, fontSize: 14 }}
    >
      <UMIcon name={icon} size={20} color={UMR.textSecondary} />
      <span style={{ display: 'flex', flexDirection: 'column' }}>
        <span>{children}</span>
        {hint && <span style={{ fontSize: 12, color: UMR.textSecondary }}>{hint}</span>}
      </span>
    </button>
  );
}

function SpaceToggle({ value, onChange }: { value: PlanSpace; onChange: (v: PlanSpace) => void }) {
  const opts: { id: PlanSpace; icon: string; label: string; hint: string }[] = [
    { id: 'carte', icon: 'map', label: 'Carte', hint: 'Explorer et modifier les données du projet' },
    { id: 'etude', icon: 'insights', label: 'Étude', hint: 'Hypothèses, calculs et comparaison des scénarios' },
  ];
  return (
    <div role="tablist" aria-label="Espace du projet" style={{ display: 'inline-flex', border: `1px solid ${PLAN.accent}`, height: 34 }}>
      {opts.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            role="tab"
            aria-selected={active}
            title={o.hint}
            onClick={() => onChange(o.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '0 18px', border: 'none', cursor: 'pointer', fontFamily: UMR.font, fontSize: 14, fontWeight: 600,
              background: active ? PLAN.accent : UMR.white, color: active ? UMR.white : PLAN.accentStrong,
            }}
          >
            <UMIcon name={o.icon} size={18} color={active ? UMR.white : PLAN.accentStrong} />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export interface PlanHeaderProps {
  data: PlanBootstrap;
  project: PlanProject;
  space: PlanSpace;
  onSpaceChange: (v: PlanSpace) => void;
  modifiedCount: number;
  driftShown: boolean;
  onShowDrift: () => void;
  onOpenUM: () => void;
  onDuplicate: () => void;
  onShare: () => void;
  onNewFromUM: () => void;
  onSwitchProject: (name: string) => void;
}

export function PlanHeader({
  data, project, space, onSpaceChange, modifiedCount, driftShown, onShowDrift, onOpenUM, onDuplicate, onShare, onNewFromUM, onSwitchProject,
}: PlanHeaderProps) {
  const [menu, setMenu] = useState<'project' | 'drift' | null>(null);
  const toggle = (m: 'project' | 'drift') => setMenu((c) => (c === m ? null : m));

  return (
    <header
      style={{
        height: PLAN.headerHeight, minHeight: PLAN.headerHeight, display: 'flex', alignItems: 'center', gap: 16, padding: '0 16px 0 20px',
        borderTop: `3px solid ${PLAN.accent}`, borderBottom: `1px solid ${UMR.border}`, background: UMR.white, fontFamily: UMR.font, position: 'relative', zIndex: 50, boxSizing: 'border-box',
      }}
    >
      <PlanLogo size={36} />

      <div style={{ position: 'relative' }}>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={menu === 'project'}
          onClick={() => toggle('project')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px 6px', fontFamily: UMR.font, textAlign: 'left' }}
        >
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 15, fontWeight: 700, lineHeight: '20px', color: UMR.textStrong }}>{project.name}</span>
            <span style={{ fontSize: 12, lineHeight: '16px', color: UMR.textSecondary, display: 'flex', alignItems: 'center', gap: 4 }}>
              <UMIcon name="content_copy" size={13} color={PLAN.accent} />
              Copie UrbanMap du {project.snapshotDate} · {project.perimeterLabel}
              {modifiedCount > 0 && <> · <span style={{ color: PLAN.map.modified, fontWeight: 600 }}>{modifiedCount} modif.</span></>}
            </span>
          </span>
          <UMIcon name="arrow_drop_down" family="icons" size={22} color={UMR.textSecondary} />
        </button>
        {menu === 'project' && (
          <Popover width={380}>
            <div style={{ padding: 16, borderBottom: `1px solid ${UMR.borderSubtle}`, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{project.name}</div>
              <div style={{ color: UMR.textSecondary }}>
                {project.networkKind === 'existing' ? 'Extension de réseau existant' : 'Réseau neuf'} · Propriétaire : {project.owner}
              </div>
              <div style={{ color: UMR.textSecondary }}>Partagé avec : {project.sharedWith.join(', ')}</div>
            </div>
            <MenuItem icon="content_copy" hint="Tout est copié : données, modifications, scénarios" onClick={() => { setMenu(null); onDuplicate(); }}>Dupliquer le projet</MenuItem>
            <MenuItem icon="share" onClick={() => { setMenu(null); onShare(); }}>Partager…</MenuItem>
            <div style={{ padding: '8px 16px 4px', fontSize: 12, color: UMR.textSecondary, borderTop: `1px solid ${UMR.borderSubtle}` }}>Autres projets</div>
            {data.otherProjects.map((p) => (
              <MenuItem key={p.id} icon="folder" hint={`Copie du ${p.snapshotDate} · ${p.owner}`} onClick={() => { setMenu(null); onSwitchProject(p.name); }}>{p.name}</MenuItem>
            ))}
            <div style={{ borderTop: `1px solid ${UMR.borderSubtle}` }}>
              <MenuItem icon="add" hint="Choisir un nouveau périmètre dans UrbanMap" onClick={() => { setMenu(null); onNewFromUM(); }}>Nouveau projet depuis UrbanMap</MenuItem>
            </div>
          </Popover>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <SpaceToggle value={space} onChange={onSpaceChange} />
      </div>

      <div style={{ position: 'relative' }}>
        <Pill tone="warning" icon="update" onClick={() => toggle('drift')} title="Information uniquement — aucune synchronisation">
          UrbanMap a évolué depuis la copie · {project.umDrift.length} bâtiments
        </Pill>
        {menu === 'drift' && (
          <Popover align="right" width={400}>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8, borderBottom: `1px solid ${UMR.borderSubtle}` }}>
              <div style={{ fontWeight: 700 }}>{project.umDrift.length} bâtiments ont changé dans UrbanMap</div>
              <div style={{ fontSize: 13, color: UMR.textSecondary }}>
                Depuis la copie du {project.snapshotDate}. <strong>Information uniquement</strong> : votre projet n’est pas modifié
                et vos calculs restent basés sur la copie.
              </div>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: '4px 0', maxHeight: 220, overflowY: 'auto' }}>
              {project.umDrift.map((d) => (
                <li key={d.functionalId} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 16px', fontSize: 13 }}>
                  <span style={{ fontFamily: 'monospace', color: UMR.blueLink }}>{d.functionalId}</span>
                  <span style={{ color: UMR.textSecondary, textAlign: 'right' }}>{d.change}</span>
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: `1px solid ${UMR.borderSubtle}` }}>
              <button
                type="button"
                onClick={() => { setMenu(null); onShowDrift(); }}
                style={{ flex: 1, height: 32, border: `1px solid ${PLAN.accent}`, background: UMR.white, color: PLAN.accentStrong, cursor: 'pointer', fontFamily: UMR.font, fontSize: 13 }}
              >
                {driftShown ? 'Masquer sur la carte' : 'Voir sur la carte'}
              </button>
              <button
                type="button"
                disabled
                title="Hors V1 : synchronisation au choix (Vx)"
                style={{ flex: 1, height: 32, border: `1px solid ${UMR.border}`, background: UMR.grey100, color: UMR.textDisabled, fontFamily: UMR.font, fontSize: 13, cursor: 'not-allowed' }}
              >
                Mettre à jour (bientôt)
              </button>
            </div>
          </Popover>
        )}
      </div>

      <button
        type="button"
        onClick={onShare}
        title="Partager le projet"
        style={{ display: 'flex', alignItems: 'center', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
      >
        {['CM', 'JB', '+1'].map((i, k) => (
          <span
            key={i}
            style={{
              width: 28, height: 28, borderRadius: 14, marginLeft: k ? -8 : 0, border: `2px solid ${UMR.white}`, background: k === 0 ? PLAN.accent : UMR.grey100,
              color: k === 0 ? UMR.white : UMR.textSecondary, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {i}
          </span>
        ))}
      </button>

      <button
        type="button"
        onClick={onOpenUM}
        style={{ display: 'flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', border: `1px solid ${UMR.border}`, background: UMR.white, color: UMR.blueLink, cursor: 'pointer', fontFamily: UMR.font, fontSize: 13 }}
      >
        <UMIcon name="open_in_new" size={16} color={UMR.blueLink} />
        Ouvrir dans UrbanMap
      </button>
    </header>
  );
}
